import React, { useEffect, useMemo, useState } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Alert, Badge, Card, Col, ProgressBar, Row, Spinner } from "react-bootstrap";
import ReactApexChart from "react-apexcharts";
import axiosInstance from "../services/axiosInterceptor";

const MONTH_NAMES = ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];

const toNumber = (value, fallback = 0) => {
  const next = Number(value);
  return Number.isFinite(next) ? next : fallback;
};

const formatNumber = (value) => toNumber(value).toLocaleString("th-TH");
const formatPercent = (value) => `${toNumber(value)}%`;
const formatBaht = (value) => `฿${toNumber(value).toLocaleString("th-TH", { maximumFractionDigits: 0 })}`;
const SEMESTER_LABEL = (semester) => `ภาคเรียนที่ ${semester ?? "-"}`;
const formatShortThaiDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  return date.toLocaleDateString("th-TH", { day: "numeric", month: "short" });
};

const getDateKey = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value.slice(0, 10);
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  return date.toISOString().slice(0, 10);
};

const pick = (obj, keys, fallback = 0) => {
  for (const key of keys) {
    if (obj && obj[key] !== undefined && obj[key] !== null) return obj[key];
  }
  return fallback;
};

const pickNested = (obj, paths, fallback = 0) => {
  for (const path of paths) {
    const value = path.split('.').reduce((current, key) => current?.[key], obj);
    if (value !== undefined && value !== null) return value;
  }
  return fallback;
};

function calcPercent(part, total) {
  const totalNumber = toNumber(total);
  if (!totalNumber) return 0;
  return Math.round((toNumber(part) / totalNumber) * 100);
}

function departmentStatus(dept) {
  if (dept?.status) return dept.status;
  const overdue = toNumber(pick(dept, ["overdue", "overdueIssues", "overdue_count"]));
  const inProgress = toNumber(pick(dept, ["inProgress", "in_progress", "inProgressIssues"]));
  const open = toNumber(pick(dept, ["open", "openIssues", "open_count"]));

  if (overdue > 0) return "danger";
  if (open + inProgress >= 10) return "warning";
  if (open + inProgress > 0) return "info";
  return "success";
}

function statusMeta(status) {
  const normalized = String(status || "").toLowerCase();
  if (["danger", "critical", "red", "overdue"].includes(normalized)) return { variant: "danger", label: "ต้องติดตามด่วน" };
  if (["warning", "yellow", "medium"].includes(normalized)) return { variant: "warning", label: "ควรติดตาม" };
  if (["info", "progress", "in_progress"].includes(normalized)) return { variant: "info", label: "กำลังดำเนินการ" };
  return { variant: "success", label: "ปกติ" };
}

const LEAVE_TYPE_PALETTE = ["#0d6efd", "#20c997", "#6f42c1", "#0dcaf0", "#d63384", "#6c757d"];

// Map a Thai leave-type name to a stable colour. Common types get a fixed hue;
// anything else cycles through the palette by index so colours stay consistent.
function leaveTypeColor(name, index = 0) {
  const label = String(name || "");
  if (label.includes("ป่วย")) return "#dc3545";   // sick
  if (label.includes("กิจ")) return "#fd7e14";    // personal
  if (label.includes("คลอด")) return "#d63384";   // maternity
  if (label.includes("พักผ่อน")) return "#198754"; // vacation
  return LEAVE_TYPE_PALETTE[index % LEAVE_TYPE_PALETTE.length];
}

function KpiCard({ icon, color = "primary", label, value, sub }) {
  return (
    <Card className="card-one h-100">
      <Card.Body className="d-flex align-items-center gap-3">
        <div className={`avatar bg-${color} text-white rounded-circle`} style={{ width: 46, height: 46 }}>
          <i className={icon} style={{ fontSize: 24 }} />
        </div>
        <div className="flex-fill">
          <h3 className="card-value mb-0">{value}</h3>
          <label className="card-title fs-sm text-secondary mb-0">{label}</label>
          {sub && <div className="fs-xs text-secondary mt-1">{sub}</div>}
        </div>
      </Card.Body>
    </Card>
  );
}

function EmptyState({ message = "ยังไม่มีข้อมูลสำหรับแสดงผล" }) {
  return (
    <div className="text-center text-secondary py-5">
      <i className="ri-inbox-line d-block mb-2" style={{ fontSize: 36 }} />
      {message}
    </div>
  );
}

export default function ExecutiveDashboard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError("");

    axiosInstance.get("/api/executive/dashboard-overview")
      .then((res) => {
        if (mounted) setData(res.data || null);
      })
      .catch((err) => {
        console.error("Executive dashboard fetch error:", err);
        if (mounted) setError(err?.response?.data?.message || "ไม่สามารถโหลดข้อมูล Dashboard ผู้บริหารได้");
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => { mounted = false; };
  }, []);

  const summary = data?.schoolSummary || {};
  const attendanceToday = data?.attendanceToday || {};
  const roomCheckToday = data?.roomCheckToday || {};
  const issueSummary = data?.issueSummary || {};
  const dormitorySummary = data?.dormitorySummary || {};
  const departmentHealth = useMemo(() => (
    Array.isArray(data?.departmentHealth) ? data.departmentHealth : []
  ), [data]);
  const attendanceRiskSevenDays = useMemo(() => (
    Array.isArray(data?.attendanceRiskSevenDays) ? data.attendanceRiskSevenDays : []
  ), [data]);
  const departmentBacklogChart = useMemo(() => (
    Array.isArray(data?.departmentBacklogChart) ? data.departmentBacklogChart : departmentHealth
  ), [data, departmentHealth]);
  const trend = useMemo(() => (
    Array.isArray(data?.attendanceMonthlyTrend) ? data.attendanceMonthlyTrend : []
  ), [data]);
  const watchlist = data?.watchlist || {};
  const displayYear = typeof data?.year === "object"
    ? (data?.year?.year_year || data?.year?.year || "-")
    : (data?.year || "-");
  const displayPeriod = typeof data?.period === "object"
    ? (data?.period?.period || data?.period?.name || data?.period?.term || (data?.period?.from && data?.period?.to ? `${formatShortThaiDate(data.period.from)} - ${formatShortThaiDate(data.period.to)}` : ""))
    : (data?.period || "");

  const students = pickNested(summary, ["students.total", "studentTotal", "totalStudents", "student_count"]);
  const teachers = pickNested(summary, ["teachers.total", "teacherTotal", "totalTeachers", "teacher_count"]);
  const rooms = pickNested(summary, ["rooms.total", "roomTotal", "totalRooms", "room_count"]);

  const present = pick(attendanceToday, ["present", "present_count"]);
  const absent = pick(attendanceToday, ["absent", "absent_count"]);
  const late = pick(attendanceToday, ["late", "late_count"]);
  const leave = pick(attendanceToday, ["leave", "leave_count"]);
  const attendanceTotal = pick(attendanceToday, ["total", "checked", "checkedTotal"], toNumber(present) + toNumber(absent) + toNumber(late) + toNumber(leave));
  const attendanceRate = pick(attendanceToday, ["attendanceRate", "presentRate", "rate"], calcPercent(present, attendanceTotal));

  const roomChecked = pick(roomCheckToday, ["checked", "checkedRooms", "checked_count"]);
  const roomTotal = pick(roomCheckToday, ["total", "totalRooms", "roomTotal"], rooms);
  const roomPercent = pick(roomCheckToday, ["percent", "rate", "checkPercent"], calcPercent(roomChecked, roomTotal));

  const issueOpen = pick(issueSummary, ["open", "openIssues", "open_count"]);
  const issueInProgress = pick(issueSummary, ["inProgress", "in_progress", "inProgressIssues"], 0);
  const issueBacklog = pick(issueSummary, ["backlog", "backlogIssues", "backlog_count"], toNumber(issueOpen) + toNumber(issueInProgress));
  const issueOverdue = pick(issueSummary, ["overdue", "overdueIssues", "overdue_count"]);

  const staffLeaveToday = data?.staffLeaveToday || {};
  const staffTotal = toNumber(pick(staffLeaveToday, ["totalStaff", "total"], teachers));
  const staffOnLeave = toNumber(pick(staffLeaveToday, ["onLeave", "on_leave"], 0));
  const staffOffsite = toNumber(pick(staffLeaveToday, ["offsite"], 0));
  const staffPresent = Math.max(staffTotal - staffOnLeave, 0);
  const staffLeaveByType = useMemo(() => (
    Array.isArray(staffLeaveToday?.byType) ? staffLeaveToday.byType : []
  ), [staffLeaveToday]);
  const staffLeavePeople = useMemo(() => (
    Array.isArray(staffLeaveToday?.people) ? staffLeaveToday.people : []
  ), [staffLeaveToday]);

  const dormOverview = dormitorySummary?.overview || dormitorySummary || {};
  const dormOccupancy = pick(dormOverview, ["occupancyRate", "occupancy_rate", "percent"], null);
  const dormCurrent = pick(dormOverview, ["current", "currentOccupancy", "current_occupancy", "occupied"], 0);
  const dormCapacity = pick(dormOverview, ["capacity", "totalCapacity", "total_capacity"], 0);

  const feeSummary = data?.feeSummary || {};
  const feeAvailable = feeSummary?.available === true;
  const feeOverall = feeSummary?.overall || {};
  const feeOverdue = feeSummary?.overdue || {};
  const feeByTerm = useMemo(() => (
    Array.isArray(data?.feeSummary?.byTerm) ? data.feeSummary.byTerm : []
  ), [data]);
  const feeTotal = toNumber(pick(feeOverall, ["total_amount", "totalAmount"]));
  const feePaid = toNumber(pick(feeOverall, ["paid_amount", "paidAmount"]));
  const feeOutstanding = toNumber(pick(feeOverall, ["outstanding", "balance"]));
  const feeCollectionRate = pick(feeOverall, ["collectionRate", "collection_rate"], calcPercent(feePaid, feeTotal));
  const feePaidCount = toNumber(pick(feeOverall, ["paid_count", "paidCount"]));
  const feePartialCount = toNumber(pick(feeOverall, ["partial_count", "partialCount"]));
  const feeUnpaidCount = toNumber(pick(feeOverall, ["unpaid_count", "unpaidCount"]));
  const feeInvoiceCount = toNumber(pick(feeOverall, ["invoice_count", "invoiceCount"], feePaidCount + feePartialCount + feeUnpaidCount));
  const feeOverdueCount = toNumber(pick(feeOverdue, ["count", "overdue_count"]));
  const feeOverdueAmount = toNumber(pick(feeOverdue, ["amount", "overdue_amount"]));
  const feeHasData = feeAvailable && feeInvoiceCount > 0;

  const monthlySeries = useMemo(() => {
    const map = {};
    trend.forEach((item) => {
      const month = toNumber(pick(item, ["month", "att_month", "periodMonth"]));
      if (month) map[month] = item;
    });
    return [
      { name: "มาเรียน", data: MONTH_NAMES.map((_, index) => toNumber(pick(map[index + 1], ["present", "present_count"]))) },
      { name: "ขาด", data: MONTH_NAMES.map((_, index) => toNumber(pick(map[index + 1], ["absent", "absent_count"]))) },
      { name: "สาย", data: MONTH_NAMES.map((_, index) => toNumber(pick(map[index + 1], ["late", "late_count"]))) },
    ];
  }, [trend]);

  const sevenDayAttendance = useMemo(() => {
    const toDate = new Date(data?.period?.to || new Date());
    const map = {};
    attendanceRiskSevenDays.forEach((item) => {
      map[getDateKey(item.att_date || item.date || item.day)] = item;
    });

    return Array.from({ length: 7 }, (_, index) => {
      const date = new Date(toDate);
      date.setDate(toDate.getDate() - (6 - index));
      const key = date.toISOString().slice(0, 10);
      const item = map[key] || {};
      return {
        date: key,
        label: formatShortThaiDate(key),
        absent: toNumber(pick(item, ["absent", "absent_count"])),
        late: toNumber(pick(item, ["late", "late_count"])),
      };
    });
  }, [attendanceRiskSevenDays, data]);

  const attendanceRiskSeries = useMemo(() => ([
    { name: "ขาด", data: sevenDayAttendance.map((item) => item.absent) },
    { name: "สาย", data: sevenDayAttendance.map((item) => item.late) },
  ]), [sevenDayAttendance]);

  const attendanceRiskHasData = useMemo(() => (
    attendanceRiskSeries.some((series) => series.data.some((value) => toNumber(value) > 0))
  ), [attendanceRiskSeries]);

  const departmentBacklogSeries = useMemo(() => ([
    { name: "เปิดใหม่", data: departmentBacklogChart.map((dept) => toNumber(pick(dept, ["open", "openIssues", "open_count"]))) },
    { name: "กำลังทำ", data: departmentBacklogChart.map((dept) => toNumber(pick(dept, ["inProgress", "in_progress", "inProgressIssues"]))) },
    { name: "เกินกำหนด", data: departmentBacklogChart.map((dept) => toNumber(pick(dept, ["overdue", "overdueIssues", "overdue_count"]))) },
  ]), [departmentBacklogChart]);

  const departmentBacklogHasData = useMemo(() => (
    departmentBacklogSeries.some((series) => series.data.some((value) => toNumber(value) > 0))
  ), [departmentBacklogSeries]);

  const donutOptions = useMemo(() => ({
    chart: { type: "donut" },
    labels: ["มาเรียน", "ขาด", "สาย", "ลา"],
    colors: ["#198754", "#dc3545", "#fd7e14", "#0d6efd"],
    legend: { position: "bottom", fontSize: "12px" },
    dataLabels: { enabled: false },
    plotOptions: {
      pie: {
        donut: {
          size: "72%",
          labels: {
            show: true,
            total: { show: true, label: "เข้าเรียน", formatter: () => formatPercent(attendanceRate) },
          },
        },
      },
    },
    stroke: { width: 2 },
  }), [attendanceRate]);

  const staffLeaveDonutOptions = useMemo(() => ({
    chart: { type: "donut" },
    labels: staffLeaveByType.map((row) => row.lt_name || "ไม่ระบุ"),
    colors: staffLeaveByType.map((row, index) => leaveTypeColor(row.lt_name, index)),
    legend: { position: "bottom", fontSize: "12px" },
    dataLabels: { enabled: false },
    plotOptions: {
      pie: {
        donut: {
          size: "72%",
          labels: {
            show: true,
            total: { show: true, label: "ลาวันนี้", formatter: () => `${formatNumber(staffOnLeave)} คน` },
          },
        },
      },
    },
    stroke: { width: 2 },
  }), [staffLeaveByType, staffOnLeave]);

  const barOptions = useMemo(() => ({
    chart: { type: "bar", toolbar: { show: false } },
    colors: ["#198754", "#dc3545", "#fd7e14"],
    plotOptions: { bar: { borderRadius: 3, columnWidth: "56%" } },
    dataLabels: { enabled: false },
    legend: { position: "top", fontSize: "12px" },
    xaxis: { categories: MONTH_NAMES, labels: { style: { fontSize: "11px" } } },
    yaxis: { labels: { style: { colors: "#6e7985", fontSize: "11px" } } },
    grid: { borderColor: "rgba(72,94,144,0.08)" },
    tooltip: { shared: true, intersect: false },
  }), []);

  const sevenDayRiskOptions = useMemo(() => ({
    chart: { type: "area", toolbar: { show: false } },
    colors: ["#dc3545", "#fd7e14"],
    dataLabels: { enabled: false },
    stroke: { curve: "smooth", width: 3 },
    fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.25, opacityTo: 0.05 } },
    legend: { position: "top", fontSize: "12px" },
    xaxis: { categories: sevenDayAttendance.map((item) => item.label), labels: { style: { fontSize: "11px" } } },
    yaxis: { min: 0, forceNiceScale: true, labels: { style: { colors: "#6e7985", fontSize: "11px" } } },
    grid: { borderColor: "rgba(72,94,144,0.08)" },
    tooltip: { shared: true, intersect: false },
  }), [sevenDayAttendance]);

  const departmentBacklogOptions = useMemo(() => ({
    chart: { type: "bar", stacked: true, toolbar: { show: false } },
    colors: ["#0d6efd", "#0dcaf0", "#dc3545"],
    plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: "58%" } },
    dataLabels: { enabled: false },
    legend: { position: "top", fontSize: "12px" },
    xaxis: {
      categories: departmentBacklogChart.map((dept) => dept.dept_name || dept.department || dept.departmentName || dept.name || "ไม่ระบุฝ่าย"),
      labels: { style: { fontSize: "11px" } },
    },
    yaxis: { labels: { style: { colors: "#6e7985", fontSize: "11px" } } },
    grid: { borderColor: "rgba(72,94,144,0.08)" },
    tooltip: { shared: true, intersect: false },
  }), [departmentBacklogChart]);

  const uncheckedRooms = Array.isArray(watchlist?.uncheckedRooms) ? watchlist.uncheckedRooms : [];
  const issueBacklogList = Array.isArray(watchlist?.issueBacklog) ? watchlist.issueBacklog : [];
  const hasData = !!data && Object.keys(data).length > 0;

  return (
    <React.Fragment>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <label className="main-title-label">Executive Dashboard</label>
            <h2 className="main-title mb-1">Dashboard ผู้บริหาร</h2>
            <p className="text-secondary mb-0">ภาพรวมสถานศึกษา ปีการศึกษา {displayYear} {displayPeriod ? `ภาคเรียน ${displayPeriod}` : ""}</p>
          </div>
          <Badge bg="primary" className="fs-sm mt-3 mt-sm-0">อ่านอย่างเดียว</Badge>
        </div>

        {loading && (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <div className="text-secondary mt-3">กำลังโหลดข้อมูล...</div>
          </div>
        )}

        {!loading && error && <Alert variant="danger">{error}</Alert>}
        {!loading && !error && !hasData && <EmptyState />}

        {!loading && !error && hasData && (
          <>
            <Row className="g-3 mb-4">
              <Col sm={6} xl={3}><KpiCard icon="ri-group-fill" color="primary" label="นักเรียนทั้งหมด" value={formatNumber(students)} /></Col>
              <Col sm={6} xl={3}><KpiCard icon="ri-user-star-fill" color="success" label="ครู / บุคลากร" value={formatNumber(teachers)} /></Col>
              <Col sm={6} xl={3}><KpiCard icon="ri-door-open-fill" color="warning" label="ห้องเรียน" value={formatNumber(rooms)} /></Col>
              <Col sm={6} xl={3}><KpiCard icon="ri-calendar-check-fill" color={toNumber(attendanceRate) >= 80 ? "success" : "danger"} label="อัตรามาเรียนวันนี้" value={formatPercent(attendanceRate)} sub={`เช็คชื่อแล้ว ${formatNumber(attendanceTotal)} รายการ`} /></Col>
              <Col sm={6} xl={3}><KpiCard icon="ri-checkbox-circle-fill" color={toNumber(roomPercent) >= 80 ? "success" : "warning"} label="เช็คชื่อครบห้อง" value={formatPercent(roomPercent)} sub={`${formatNumber(roomChecked)} / ${formatNumber(roomTotal)} ห้อง`} /></Col>
              <Col sm={6} xl={3}><KpiCard icon="ri-tools-fill" color={toNumber(issueOverdue) > 0 ? "danger" : "info"} label="เรื่องเปิด / ค้าง" value={`${formatNumber(issueOpen)} / ${formatNumber(issueBacklog)}`} sub={toNumber(issueOverdue) > 0 ? `เกินกำหนด ${formatNumber(issueOverdue)} เรื่อง` : "ไม่มีรายการเกินกำหนด"} /></Col>
              {dormOccupancy !== null && (
                <Col sm={6} xl={3}><KpiCard icon="ri-building-4-fill" color="secondary" label="อัตราเข้าพักหอพัก" value={formatPercent(dormOccupancy)} sub={`${formatNumber(dormCurrent)} / ${formatNumber(dormCapacity)} คน`} /></Col>
              )}
            </Row>

            <div className="d-flex align-items-center gap-2 mb-3">
              <i className="ri-team-line text-primary" style={{ fontSize: 22 }} />
              <h5 className="mb-0">สรุปบุคลากรประจำวัน</h5>
              <span className="fs-xs text-secondary">ข้อมูล ณ วันที่ {formatShortThaiDate(data?.period?.to)}</span>
            </div>

            <Row className="g-3 mb-4">
              <Col sm={6} xl={3}><KpiCard icon="ri-user-3-fill" color="primary" label="บุคลากรทั้งหมด" value={formatNumber(staffTotal)} /></Col>
              <Col sm={6} xl={3}><KpiCard icon="ri-user-follow-fill" color="success" label="มาปฏิบัติงาน" value={formatNumber(staffPresent)} sub={`คิดเป็น ${formatPercent(calcPercent(staffPresent, staffTotal))}`} /></Col>
              <Col sm={6} xl={3}><KpiCard icon="ri-user-unfollow-fill" color={staffOnLeave > 0 ? "warning" : "secondary"} label="ลาวันนี้" value={formatNumber(staffOnLeave)} sub={staffLeaveByType.length > 0 ? staffLeaveByType.map((row) => `${row.lt_name} ${formatNumber(row.count)}`).join(" · ") : "ไม่มีผู้ลา"} /></Col>
              <Col sm={6} xl={3}><KpiCard icon="ri-walk-fill" color={staffOffsite > 0 ? "info" : "secondary"} label="ออกนอกสถานที่" value={formatNumber(staffOffsite)} sub="ขออนุญาตออกนอกสถานที่วันนี้" /></Col>
            </Row>

            <Row className="g-3 mb-4">
              <Col xl={5}>
                <Card className="card-one h-100">
                  <Card.Header><Card.Title as="h6">การลาแยกตามประเภท</Card.Title></Card.Header>
                  <Card.Body>
                    {staffOnLeave > 0 && staffLeaveByType.length > 0 ? (
                      <ReactApexChart options={staffLeaveDonutOptions} series={staffLeaveByType.map((row) => toNumber(row.count))} type="donut" height={280} />
                    ) : <EmptyState message="วันนี้ไม่มีบุคลากรลา" />}
                  </Card.Body>
                </Card>
              </Col>

              <Col xl={7}>
                <Card className="card-one h-100">
                  <Card.Header>
                    <Card.Title as="h6">รายชื่อบุคลากรที่ลาวันนี้</Card.Title>
                    <div className="fs-xs text-secondary">เฉพาะการลาที่อนุมัติแล้วและครอบคลุมวันนี้</div>
                  </Card.Header>
                  <Card.Body>
                    {staffLeavePeople.length === 0 ? (
                      <EmptyState message="วันนี้ไม่มีบุคลากรลา" />
                    ) : staffLeavePeople.map((person, index) => (
                      <div key={person.t_id || index} className="d-flex align-items-center justify-content-between border-bottom py-2 fs-sm">
                        <span className="d-flex align-items-center gap-2">
                          <span style={{ width: 10, height: 10, borderRadius: "50%", background: leaveTypeColor(person.lt_name, index), display: "inline-block" }} />
                          {person.t_name || "ไม่ระบุชื่อ"}
                        </span>
                        <span className="d-flex align-items-center gap-3">
                          <Badge bg="light" text="dark" className="fw-normal">{person.lt_name || "ไม่ระบุ"}</Badge>
                          <span className="text-secondary fs-xs">{formatShortThaiDate(person.lr_start_date)} - {formatShortThaiDate(person.lr_end_date)}</span>
                        </span>
                      </div>
                    ))}
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {feeAvailable && (
              <>
                <div className="d-flex align-items-center gap-2 mb-3">
                  <i className="ri-money-dollar-circle-line text-success" style={{ fontSize: 22 }} />
                  <h5 className="mb-0">การเงิน / ค่าธรรมเนียม</h5>
                  <span className="fs-xs text-secondary">ปีการศึกษา {displayYear}</span>
                </div>

                <Row className="g-3 mb-4">
                  <Col sm={6} xl={3}><KpiCard icon="ri-bill-line" color="primary" label="ยอดเรียกเก็บทั้งหมด" value={formatBaht(feeTotal)} sub={`${formatNumber(feeInvoiceCount)} ใบแจ้งหนี้`} /></Col>
                  <Col sm={6} xl={3}><KpiCard icon="ri-hand-coin-line" color="success" label="เก็บได้แล้ว" value={formatBaht(feePaid)} sub={`อัตราเก็บเงิน ${formatPercent(feeCollectionRate)}`} /></Col>
                  <Col sm={6} xl={3}><KpiCard icon="ri-wallet-3-line" color={feeOutstanding > 0 ? "warning" : "success"} label="ค้างชำระ" value={formatBaht(feeOutstanding)} sub={`${formatNumber(feeUnpaidCount + feePartialCount)} รายที่ยังค้าง`} /></Col>
                  <Col sm={6} xl={3}><KpiCard icon="ri-alarm-warning-line" color={feeOverdueCount > 0 ? "danger" : "secondary"} label="เกินกำหนดชำระ" value={formatNumber(feeOverdueCount)} sub={feeOverdueCount > 0 ? `ยอด ${formatBaht(feeOverdueAmount)}` : "ไม่มีรายการเกินกำหนด"} /></Col>
                </Row>

                <Row className="g-3 mb-4">
                  <Col xl={5}>
                    <Card className="card-one h-100">
                      <Card.Header><Card.Title as="h6">สถานะการชำระเงิน</Card.Title></Card.Header>
                      <Card.Body>
                        {feeHasData ? (
                          <>
                            <div className="d-flex justify-content-between align-items-end mb-1">
                              <span className="fs-sm text-secondary">อัตราการเก็บเงิน</span>
                              <span className="fw-semibold fs-4">{formatPercent(feeCollectionRate)}</span>
                            </div>
                            <ProgressBar className="mb-3" style={{ height: 10 }} now={toNumber(feeCollectionRate)} variant={toNumber(feeCollectionRate) >= 80 ? "success" : toNumber(feeCollectionRate) >= 50 ? "warning" : "danger"} />
                            <div className="d-flex justify-content-between border-bottom py-2 fs-sm">
                              <span><i className="ri-checkbox-circle-fill text-success me-1" />ชำระครบแล้ว</span>
                              <span className="fw-semibold">{formatNumber(feePaidCount)} ราย</span>
                            </div>
                            <div className="d-flex justify-content-between border-bottom py-2 fs-sm">
                              <span><i className="ri-progress-5-line text-warning me-1" />ชำระบางส่วน</span>
                              <span className="fw-semibold">{formatNumber(feePartialCount)} ราย</span>
                            </div>
                            <div className="d-flex justify-content-between py-2 fs-sm">
                              <span><i className="ri-close-circle-line text-danger me-1" />ยังไม่ชำระ</span>
                              <span className="fw-semibold">{formatNumber(feeUnpaidCount)} ราย</span>
                            </div>
                          </>
                        ) : <EmptyState message="ยังไม่มีใบแจ้งหนี้ในปีการศึกษานี้" />}
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col xl={7}>
                    <Card className="card-one h-100">
                      <Card.Header>
                        <Card.Title as="h6">สถานะการชำระรายภาคเรียน</Card.Title>
                        <div className="fs-xs text-secondary">เปรียบเทียบยอดเก็บได้กับยอดเรียกเก็บแต่ละภาคเรียน</div>
                      </Card.Header>
                      <Card.Body>
                        {feeByTerm.length === 0 ? <EmptyState message="ยังไม่มีข้อมูลรายภาคเรียน" /> : feeByTerm.map((term) => {
                          const rate = pick(term, ["collectionRate", "collection_rate"], calcPercent(term.paid_amount, term.total_amount));
                          return (
                            <div key={term.term_id} className="border rounded p-3 mb-2">
                              <div className="d-flex align-items-center justify-content-between mb-2">
                                <strong>{term.term_name || SEMESTER_LABEL(term.semester)}</strong>
                                <Badge bg={toNumber(rate) >= 80 ? "success" : toNumber(rate) >= 50 ? "warning" : "danger"}>{formatPercent(rate)}</Badge>
                              </div>
                              <ProgressBar className="mb-2" style={{ height: 8 }} now={toNumber(rate)} variant={toNumber(rate) >= 80 ? "success" : toNumber(rate) >= 50 ? "warning" : "danger"} />
                              <div className="d-flex gap-3 flex-wrap fs-xs text-secondary">
                                <span>เรียกเก็บ {formatBaht(term.total_amount)}</span>
                                <span className="text-success">เก็บได้ {formatBaht(term.paid_amount)}</span>
                                <span className="text-danger">ค้าง {formatBaht(term.outstanding)}</span>
                                <span>{formatNumber(term.invoice_count)} ใบ</span>
                              </div>
                            </div>
                          );
                        })}
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </>
            )}

            <Row className="g-3 mb-4">
              <Col xl={4}>
                <Card className="card-one h-100">
                  <Card.Header><Card.Title as="h6">การเข้าเรียนวันนี้</Card.Title></Card.Header>
                  <Card.Body>
                    {toNumber(attendanceTotal) > 0 ? (
                      <ReactApexChart options={donutOptions} series={[toNumber(present), toNumber(absent), toNumber(late), toNumber(leave)]} type="donut" height={280} />
                    ) : <EmptyState message="ยังไม่มีข้อมูลเช็คชื่อวันนี้" />}
                  </Card.Body>
                </Card>
              </Col>
              <Col xl={8}>
                <Card className="card-one h-100">
                  <Card.Header><Card.Title as="h6">แนวโน้มการเข้าเรียนรายเดือน</Card.Title></Card.Header>
                  <Card.Body>
                    {trend.length > 0 ? (
                      <ReactApexChart options={barOptions} series={monthlySeries} type="bar" height={280} />
                    ) : <EmptyState message="ยังไม่มีข้อมูลแนวโน้มรายเดือน" />}
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Row className="g-3 mb-4">
              <Col xl={6}>
                <Card className="card-one h-100">
                  <Card.Header>
                    <Card.Title as="h6">ขาด/สาย 7 วันล่าสุด</Card.Title>
                    <div className="fs-xs text-secondary">ช่วยดูแนวโน้มนักเรียนกลุ่มเสี่ยงระยะสั้น</div>
                  </Card.Header>
                  <Card.Body>
                    {attendanceRiskHasData ? (
                      <ReactApexChart options={sevenDayRiskOptions} series={attendanceRiskSeries} type="area" height={300} />
                    ) : <EmptyState message="ยังไม่มีข้อมูลขาด/สาย 7 วันล่าสุด" />}
                  </Card.Body>
                </Card>
              </Col>

              <Col xl={6}>
                <Card className="card-one h-100">
                  <Card.Header>
                    <Card.Title as="h6">งานค้างรายฝ่าย</Card.Title>
                    <div className="fs-xs text-secondary">แยกเปิดใหม่ / กำลังทำ / เกินกำหนด</div>
                  </Card.Header>
                  <Card.Body>
                    {departmentBacklogHasData ? (
                      <ReactApexChart options={departmentBacklogOptions} series={departmentBacklogSeries} type="bar" height={300} />
                    ) : <EmptyState message="ยังไม่มีข้อมูลงานค้างรายฝ่าย" />}
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Row className="g-3 mb-4">
              <Col xl={7}>
                <Card className="card-one h-100">
                  <Card.Header><Card.Title as="h6">สุขภาพการทำงานรายฝ่าย</Card.Title></Card.Header>
                  <Card.Body>
                    {departmentHealth.length === 0 ? <EmptyState /> : departmentHealth.map((dept, index) => {
                      const status = statusMeta(departmentStatus(dept));
                      const open = toNumber(pick(dept, ["open", "openIssues", "open_count"]));
                      const inProgress = toNumber(pick(dept, ["inProgress", "in_progress", "inProgressIssues"]));
                      const overdue = toNumber(pick(dept, ["overdue", "overdueIssues", "overdue_count"]));
                      const total = open + inProgress + overdue;
                      return (
                        <div key={dept.dept_id || dept.id || dept.department || index} className="border rounded p-3 mb-2">
                          <div className="d-flex align-items-center justify-content-between mb-2">
                            <strong>{dept.dept_name || dept.department || dept.departmentName || dept.name || "ไม่ระบุฝ่าย"}</strong>
                            <Badge bg={status.variant}>{status.label}</Badge>
                          </div>
                          <ProgressBar className="mb-2" style={{ height: 8 }}>
                            <ProgressBar variant="info" now={calcPercent(inProgress, total || 1)} key={1} />
                            <ProgressBar variant="danger" now={calcPercent(overdue, total || 1)} key={2} />
                          </ProgressBar>
                          <div className="d-flex gap-3 fs-xs text-secondary">
                            <span>เปิดอยู่ {formatNumber(open)}</span>
                            <span>กำลังทำ {formatNumber(inProgress)}</span>
                            <span>เกินกำหนด {formatNumber(overdue)}</span>
                          </div>
                        </div>
                      );
                    })}
                  </Card.Body>
                </Card>
              </Col>

              <Col xl={5}>
                <Card className="card-one h-100">
                  <Card.Header><Card.Title as="h6">Watchlist ที่ควรติดตาม</Card.Title></Card.Header>
                  <Card.Body>
                    <h6 className="fs-sm mb-2"><i className="ri-door-open-line me-1 text-warning" />ห้องที่ยังไม่เช็คชื่อ</h6>
                    {uncheckedRooms.length === 0 ? (
                      <div className="text-secondary fs-sm mb-3">ไม่มีรายการค้างเช็คชื่อ</div>
                    ) : uncheckedRooms.slice(0, 8).map((room, index) => (
                      <div key={room.r_id || room.id || index} className="d-flex justify-content-between border-bottom py-2 fs-sm">
                        <span>{room.room_label || room.roomName || room.room || room.className || "ไม่ระบุห้อง"}</span>
                        <span className="text-secondary">{room.level || room.grade || "ยังไม่เช็ค"}</span>
                      </div>
                    ))}

                    <h6 className="fs-sm mb-2 mt-4"><i className="ri-tools-line me-1 text-danger" />เรื่องค้างดำเนินการ</h6>
                    {issueBacklogList.length === 0 ? (
                      <div className="text-secondary fs-sm">ไม่มีเรื่องค้างสำคัญ</div>
                    ) : issueBacklogList.slice(0, 8).map((issue, index) => {
                      const daysOpen = pick(issue, ["days_open", "daysOpen"], null);
                      const issueCode = issue.ir_code || issue.code || `#${index + 1}`;
                      return (
                        <div key={issue.id || issue.ir_id || index} className="border rounded p-2 mb-2 fs-sm">
                          <div className="fw-semibold text-truncate">รายการค้างดำเนินการ {issueCode}</div>
                          <div className="text-secondary fs-xs d-flex justify-content-between">
                            <span>{issue.dept_name || issue.department || issue.departmentName || "ไม่ระบุฝ่าย"}</span>
                            <span>{daysOpen !== null ? `ค้าง ${formatNumber(daysOpen)} วัน` : (issue.ir_status || issue.status || issue.ageLabel || "รอติดตาม")}</span>
                          </div>
                        </div>
                      );
                    })}
                    <div className="text-secondary fs-xs mt-3">แสดงเฉพาะข้อมูลสรุป ไม่แสดงข้อมูลส่วนบุคคลของนักเรียน/ผู้แจ้ง</div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </>
        )}

        <Footer />
      </div>
    </React.Fragment>
  );
}
